/**
 * Reach HUD number formatting (plan R15): distances in m, km, Mm and AU; speeds in m/s, km/s and Mm/s; durations
 * and credits. Every scaled readout has two halves:
 * - a `…Key` function, allocation-free, that maps a value to an integer unique to the text it would show (the view
 *   diffs on it every frame);
 * - a `format…` function that builds the string, called only when the key changed (D-014).
 *
 * Precision: three significant figures (x.xx, xx.x, xxx) in every unit, except metres and m/s, which are whole.
 */

/** One display unit: its scale in base units and its suffix. */
interface Unit {
  readonly scale: number
  readonly suffix: string
  /** Whole numbers only (no decimals) in this unit. */
  readonly whole: boolean
}

/** One astronomical unit (m), IAU 2012. */
export const AU_M = 149_597_870_700

const DISTANCE_UNITS: readonly Unit[] = [
  { scale: 1, suffix: 'm', whole: true },
  { scale: 1e3, suffix: 'km', whole: false },
  { scale: 1e6, suffix: 'Mm', whole: false },
  { scale: AU_M, suffix: 'AU', whole: false },
]

/** Far nav markers read in metric prefixes only (D-249): m, km, Mm, Gm. */
const NAV_UNITS: readonly Unit[] = [
  { scale: 1, suffix: 'm', whole: true },
  { scale: 1e3, suffix: 'km', whole: false },
  { scale: 1e6, suffix: 'Mm', whole: false },
  { scale: 1e9, suffix: 'Gm', whole: false },
]

const SPEED_UNITS: readonly Unit[] = [
  { scale: 1, suffix: 'm/s', whole: true },
  { scale: 1e3, suffix: 'km/s', whole: false },
  { scale: 1e6, suffix: 'Mm/s', whole: false },
]

/** Megametres switch to AU from this distance (0.1 AU): a few Mm read better than 0.00 AU. */
const AU_FROM_M = 0.1 * AU_M

// Scratch for the last scaled pick (D-014): the unit index, the decimals and the rounded integer.
let pickUnit = 0
let pickDecimals = 0
let pickN = 0

/** Chooses the unit for `value` (≥ 0) and rounds it; writes the pick into the module scratch. */
function pick(units: readonly Unit[], value: number, auFrom: number): void {
  const v = value > 0 && Number.isFinite(value) ? value : value === Infinity ? Number.MAX_VALUE : 0
  let u = 0
  const last = units.length - 1
  while (u < last) {
    const next = units[u + 1]
    // AU is the one unit that does not start at 1000 of the previous one.
    const from = next.suffix === 'AU' ? auFrom : next.scale
    if (v < from) break
    u++
  }
  // Rounding may carry into the next unit (999.6 m → 1.00 km); re-pick once when it does.
  for (let pass = 0; pass < 2; pass++) {
    const unit = units[u]
    const x = v / unit.scale
    let d = unit.whole ? 0 : x < 10 ? 2 : x < 100 ? 1 : 0
    let n = Math.round(x * (d === 2 ? 100 : d === 1 ? 10 : 1))
    if (d === 2 && n >= 1000) {
      d = 1
      n = Math.round(x * 10)
    }
    if (d === 1 && n >= 1000) {
      d = 0
      n = Math.round(x)
    }
    const nextIsPlain = u < last && units[u + 1].suffix !== 'AU'
    if (pass === 0 && d === 0 && n >= 1000 && nextIsPlain) {
      u++
      continue
    }
    pickUnit = u
    pickDecimals = d
    pickN = n
    return
  }
}

/** A key unique to the displayed text: unit and decimals in the high part, the rounded value in the low part. */
function pickKey(): number {
  return (pickUnit * 3 + pickDecimals) * 1e12 + pickN
}

function pickText(units: readonly Unit[]): string {
  const d = pickDecimals
  const value = d === 0 ? String(pickN) : (pickN / (d === 2 ? 100 : 10)).toFixed(d)
  return `${value} ${units[pickUnit].suffix}`
}

/** The display key of a distance (m). Allocation-free. */
export function distanceKey(metres: number): number {
  pick(DISTANCE_UNITS, metres, AU_FROM_M)
  return pickKey()
}

/** A distance: "850 m", "12.3 km", "4.56 Mm", "0.31 AU". */
export function formatDistance(metres: number): string {
  pick(DISTANCE_UNITS, metres, AU_FROM_M)
  return pickText(DISTANCE_UNITS)
}

/** The display key of a far nav marker's distance (m). Allocation-free. */
export function navDistanceKey(metres: number): number {
  pick(NAV_UNITS, metres, Infinity)
  return pickKey()
}

/** A far nav marker's distance: "850 m", "53.0 km", "4.56 Mm", "38.2 Gm". */
export function formatNavDistance(metres: number): string {
  pick(NAV_UNITS, metres, Infinity)
  return pickText(NAV_UNITS)
}

/** Below this many hundreds of metres the flight HUD reads km to 0.1 (999.9 km); from it, Mm and Gm. */
const HUD_KM_TENTHS_MAX = 10_000
/** Offset that keeps the Mm/Gm keys apart from the km keys (-1 … -10 000). */
const HUD_FAR_KEY = 1e5

/**
 * The display key of a flight HUD marker distance (m) (the M1 and M2 flight HUDs; D-459 bug 10). Allocation-free:
 * metres (5 m steps, key ≥ 0) below 1 km, km to 0.1 below 1,000 km (key -1 … -10 000), then the far nav ladder.
 */
export function hudDistanceKey(metres: number): number {
  if (!(metres >= 999.5)) return Math.round((metres > 0 ? metres : 0) / 5) * 5
  const n = Math.round(metres / 100)
  if (n < HUD_KM_TENTHS_MAX) return -n - 1
  return -HUD_FAR_KEY - navDistanceKey(metres)
}

/** A flight HUD marker distance: "840 m", "9.1 km", "999.9 km", then "1.00 Mm", "39.0 Gm" (never "39005223.9 km"). */
export function formatHudDistance(metres: number): string {
  if (!(metres >= 999.5)) return `${Math.round((metres > 0 ? metres : 0) / 5) * 5} m`
  const n = Math.round(metres / 100)
  if (n < HUD_KM_TENTHS_MAX) return `${(n / 10).toFixed(1)} km`
  return formatNavDistance(metres)
}

/** The display key of a speed (m/s). Allocation-free. */
export function speedKey(mps: number): number {
  pick(SPEED_UNITS, mps, Infinity)
  return pickKey()
}

/** A speed: "180 m/s", "2.40 km/s", "19.9 Mm/s", "20000 Mm/s". */
export function formatSpeed(mps: number): string {
  pick(SPEED_UNITS, mps, Infinity)
  return pickText(SPEED_UNITS)
}

/** Whole seconds shown for a duration (rounded up, so a countdown reads 1 until it ends). Allocation-free. */
export function durationKey(seconds: number): number {
  if (!(seconds > 0)) return 0
  if (!Number.isFinite(seconds)) return -1
  return Math.ceil(seconds - 1e-9)
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/** A duration: "45 s", "1:05", "1:02:03"; "--" when unknown (infinite or NaN). */
export function formatDuration(seconds: number): string {
  const s = durationKey(seconds)
  if (s < 0 || Number.isNaN(seconds)) return '--'
  if (s < 60) return `${s} s`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  return h > 0 ? `${h}:${pad2(m)}:${pad2(r)}` : `${m}:${pad2(r)}`
}

/** Credits with thin grouping: "12,500 cr" (rounded to whole credits). */
export function formatCredits(amount: number): string {
  const n = Math.round(Number.isFinite(amount) ? amount : 0)
  const neg = n < 0
  const digits = String(neg ? -n : n)
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ','
    out += digits[i]
  }
  return `${neg ? '-' : ''}${out} cr`
}

/**
 * The display key of a far marker's cruise time (CL-8): whole seconds below `minutesFromS`, then whole minutes
 * (negative keys); 0 = no cruise time (zero, negative, NaN or infinite). Allocation-free: the view diffs on it.
 */
export function cruiseEtaKey(seconds: number, minutesFromS: number): number {
  if (!(seconds > 0) || !Number.isFinite(seconds)) return 0
  const s = Math.round(seconds)
  if (s < minutesFromS) return s < 1 ? 1 : s
  const m = Math.round(seconds / 60)
  return m < 1 ? -1 : -m
}

/**
 * A far marker's cruise time (CL-8): `secondsLabel` or `minutesLabel` with `{n}` replaced ("~12 s cruise",
 * "~3 min cruise"); '' with none. Built only when `cruiseEtaKey` changes.
 */
export function formatCruiseEta(seconds: number, minutesFromS: number, secondsLabel: string, minutesLabel: string): string {
  const k = cruiseEtaKey(seconds, minutesFromS)
  if (k === 0) return ''
  return k > 0 ? secondsLabel.replace('{n}', String(k)) : minutesLabel.replace('{n}', String(-k))
}
